<template>
  <div class="business-stream-content">
    <!-- 1. STREAM HEADER — exact same structure as character pages -->
    <header class="stream-header">
      <p class="intro-text serif smaller-text text-center largerLetterSpacing">
        BUSINESS STREAM {{ streamNumber }}
      </p>
      <h1 class="text-center largest-TextMag largeLetterSpacing">
        {{ streamName }}
      </h1>
      <p v-if="streamSubtitle" class="text-center italic shortWdithOnDesktop serif noMarginBottom" style="margin-left: auto; margin-right: auto;">
        {{ streamSubtitle }}
      </p>
      <!-- Tagline v4: Two-line cascade layout matching character pages (From Cecilia / to a real girl) -->
      <div class="stream-tagline" v-if="streamTaglineLine1 || streamTaglineLine2">
        <div class="tagline-line1 animate fadeInRight" v-if="streamTaglineLine1">
          <h2>{{ streamTaglineLine1 }}</h2>
        </div>
        <div class="tagline-line2 animate fadeInRight" v-if="streamTaglineLine2">
          <h2><span class="serif textColour">{{ streamTaglineLine2 }}</span></h2>
        </div>
      </div>
    </header>

    <!-- 2. INTRODUCTORY PARAGRAPH -->
    <section class="intro-section" v-if="introText">
      <p
        v-for="(para, idx) in introTextArray"
        :key="'intro-' + idx"
        class="intro-paragraph"
      >
        {{ para }}
      </p>

      <!-- Intro bullet points (optional) -->
      <ul
        v-if="introBulletPoints && introBulletPoints.length"
        class="intro-bullet-list"
      >
        <li
          v-for="(point, idx) in introBulletPoints"
          :key="'intro-bullet-' + idx"
          v-html="point"
        ></li>
      </ul>

      <!-- Intro closing paragraph(s) (optional) - supports array or string -->
      <template v-if="introClosing">
        <p 
          v-for="(para, idx) in (Array.isArray(introClosing) ? introClosing : [introClosing])"
          :key="'intro-closing-' + idx"
          class="intro-paragraph intro-closing"
          v-html="para"
        ></p>
      </template>

      <!-- BS7: Intro Phase bullets (optional) -->
      <ul
        v-if="introPhaseBullets && introPhaseBullets.length"
        class="intro-bullet-list"
      >
        <li
          v-for="(point, idx) in introPhaseBullets"
          :key="'intro-phase-bullet-' + idx"
          v-html="point"
        ></li>
      </ul>

      <!-- BS7: Intro final paragraph (optional) -->
      <p v-if="introFinalParagraph" class="intro-paragraph intro-final">
        {{ introFinalParagraph }}
      </p>
    </section>

    <!-- 2.5 INTRO IMAGES (optional images after intro text) -->
    <div
      v-if="introImages && introImages.length"
      class="section-images-grid intro-images"
      :class="'grid-' + introImages.length"
    >
      <div
        v-for="(img, imgIdx) in introImages"
        :key="'intro-img-' + imgIdx"
        class="grid-image-item"
      >
        <img :src="img.src" :alt="img.alt" class="grid-image" />
        <p v-if="img.caption" class="grid-image-caption" v-html="img.caption"></p>
      </div>
    </div>

    <!-- 3. NARRATIVE SECTIONS (poetic, italic, lilac/gold titles with ~) -->
    <section
      v-for="(section, idx) in narrativeSections"
      :key="'narrative-' + idx"
      class="narrative-section animate fadeInUp"
    >
      <h3 :class="['narrative-title', section.titleClass || '']">
        <span class="tilde-left">~</span>
        <span class="title-text">{{ section.title }}</span>
        <span class="tilde-right">~</span>
      </h3>
      <p v-if="section.subtitle" class="narrative-subtitle"
        v-html="section.subtitle">
      </p>
      <!-- Roadmap slot -->
      <div v-if="section.isRoadmapPlaceholder" class="roadmap-container">
        <slot name="roadmap"></slot>
      </div>
      <div v-else class="narrative-content">
        <p
          v-for="(para, pIdx) in section.paragraphs"
          :key="'n-para-' + pIdx"
          v-html="para"
        ></p>
        <ul
          v-if="section.bulletPoints && section.bulletPoints.length"
          class="narrative-bullets"
        >
          <li
            v-for="(bullet, bIdx) in section.bulletPoints"
            :key="'bullet-' + bIdx"
            :class="
              typeof bullet === 'object' && bullet.class ? bullet.class : ''
            "
            v-html="typeof bullet === 'object' ? bullet.text : bullet"
          ></li>
        </ul>
        <!-- Post-bullet paragraphs (paragraphs that come after bullet points) -->
        <p
          v-for="(para, pbIdx) in section.postBulletParagraphs"
          :key="'post-bullet-' + pbIdx"
          v-html="para"
        ></p>
        <!-- Second set of bullet points -->
        <ul
          v-if="section.secondBulletPoints && section.secondBulletPoints.length"
          class="narrative-bullets"
        >
          <li
            v-for="(bullet, sbIdx) in section.secondBulletPoints"
            :key="'second-bullet-' + sbIdx"
            :class="
              typeof bullet === 'object' && bullet.class ? bullet.class : ''
            "
            v-html="typeof bullet === 'object' ? bullet.text : bullet"
          ></li>
        </ul>
        <!-- Post-second-bullet paragraphs -->
        <p
          v-for="(para, psbIdx) in section.postSecondBulletParagraphs"
          :key="'post-second-bullet-' + psbIdx"
          v-html="para"
        ></p>
        <!-- Closing paragraph -->
        <p v-if="section.closingParagraph" class="section-closing" v-html="section.closingParagraph">
        </p>
      </div>
      <!-- Optional image for narrative section -->
      <div v-if="section.image" class="section-image-container">
        <img
          :src="section.image"
          :alt="section.imageAlt || section.title"
          class="section-image"
        />
        <p v-if="section.imageCaption" class="image-caption" v-html="section.imageCaption">
        </p>
      </div>
      <!-- Optional image grid for narrative section (2x2 or side-by-side) -->
      <div
        v-if="section.images && section.images.length"
        class="section-images-grid"
        :class="[
          section.imagesStacked ? 'grid-1' : ('grid-' + section.images.length),
          { 'vertical': section.images.length === 1 && section.images[0].vertical }
        ]"
      >
        <div
          v-for="(img, imgIdx) in section.images"
          :key="'img-' + imgIdx"
          class="grid-image-item"
        >
          <img
            :src="img.src"
            :alt="img.alt || section.title"
            class="grid-image"
          />
          <p v-if="img.caption" class="grid-image-caption" v-html="img.caption"></p>
        </div>
      </div>
      <p v-if="section.sharedImageCaption" class="grid-image-caption shared-image-caption"
        v-html="section.sharedImageCaption">
      </p>
      <!-- Post-image paragraphs (content that follows images) -->
      <p
        v-for="(para, idx) in section.postImageParagraphs"
        :key="'postimg-' + idx"
        class="narrative-paragraph"
        v-html="para"
      ></p>

      <!-- BS7: First bullet list (after post-image paragraphs) -->
      <ul
        v-if="section.firstBulletList && section.firstBulletList.length"
        class="narrative-bullets"
      >
        <li
          v-for="(bullet, fbIdx) in section.firstBulletList"
          :key="'first-bullet-' + fbIdx"
          v-html="bullet"
        ></li>
      </ul>

      <!-- BS7: Post-first-bullet paragraphs -->
      <p
        v-for="(para, pfbIdx) in section.postFirstBulletParagraphs"
        :key="'post-first-bullet-' + pfbIdx"
        class="narrative-paragraph"
        v-html="para"
      ></p>

      <!-- BS7: Second bullet list -->
      <ul
        v-if="section.secondBulletList && section.secondBulletList.length"
        class="narrative-bullets"
      >
        <li
          v-for="(bullet, sbIdx) in section.secondBulletList"
          :key="'second-bullet-list-' + sbIdx"
          v-html="bullet"
        ></li>
      </ul>

      <!-- BS7: Post-second-bullet-list paragraphs -->
      <p
        v-for="(para, psblIdx) in section.postSecondBulletListParagraphs"
        :key="'post-second-bullet-list-' + psblIdx"
        class="narrative-paragraph"
        v-html="para"
      ></p>

      <!-- BS7: Post-second-bullet image -->
      <div v-if="section.postSecondBulletImage" class="section-image-container">
        <img
          :src="section.postSecondBulletImage.src"
          :alt="section.postSecondBulletImage.alt"
          class="section-image"
        />
        <p v-if="section.postSecondBulletImage.caption" class="image-caption" v-html="section.postSecondBulletImage.caption"></p>
      </div>

      <!-- BS7: Third bullet list -->
      <ul
        v-if="section.thirdBulletList && section.thirdBulletList.length"
        class="narrative-bullets"
      >
        <li
          v-for="(bullet, tbIdx) in section.thirdBulletList"
          :key="'third-bullet-' + tbIdx"
          v-html="bullet"
        ></li>
      </ul>

      <!-- BS7: Comparison lines (styled emphasized text) -->
      <div v-if="section.comparisonLines && section.comparisonLines.length" class="comparison-lines">
        <p
          v-for="(line, clIdx) in section.comparisonLines"
          :key="'comp-' + clIdx"
          class="comparison-line"
          v-html="line"
        ></p>
      </div>

      <!-- BS7: Sensory channels list -->
      <ul
        v-if="section.sensoryChannels && section.sensoryChannels.length"
        class="narrative-bullets"
      >
        <li
          v-for="(channel, scIdx) in section.sensoryChannels"
          :key="'sensory-' + scIdx"
          v-html="channel"
        ></li>
      </ul>

      <!-- BS7: Post-sensory image -->
      <div v-if="section.postSensoryImage" class="section-image-container">
        <img
          :src="section.postSensoryImage.src"
          :alt="section.postSensoryImage.alt"
          class="section-image"
        />
        <p v-if="section.postSensoryImage.caption" class="image-caption" v-html="section.postSensoryImage.caption"></p>
      </div>

      <!-- BS7: Encoding descriptions -->
      <div v-if="section.encodingDescriptions && section.encodingDescriptions.length" class="encoding-descriptions">
        <p
          v-for="(desc, edIdx) in section.encodingDescriptions"
          :key="'encoding-' + edIdx"
          class="encoding-description"
          v-html="desc.text"
        ></p>
      </div>

      <!-- BS7: Operational priorities -->
      <ul
        v-if="section.operationalPriorities && section.operationalPriorities.length"
        class="narrative-bullets"
      >
        <li
          v-for="(priority, opIdx) in section.operationalPriorities"
          :key="'op-priority-' + opIdx"
          v-html="priority"
        ></li>
      </ul>

      <!-- BS7: Post-operational paragraphs -->
      <p
        v-for="(para, popIdx) in section.postOperationalParagraphs"
        :key="'post-op-' + popIdx"
        class="narrative-paragraph"
        v-html="para"
      ></p>

      <!-- BS7: Research vectors -->
      <div v-if="section.researchVectors && section.researchVectors.length" class="research-vectors">
        <div
          v-for="(vector, rvIdx) in section.researchVectors"
          :key="'vector-' + rvIdx"
          class="research-vector"
        >
          <p class="vector-title"><strong><span class="textColour">{{ vector.label }})</span> <span v-html="vector.title"></span></strong></p>
          <p class="vector-text">{{ vector.text }}</p>
        </div>
      </div>

      <!-- BS7: Post-vectors paragraphs -->
      <p
        v-for="(para, pvIdx) in section.postVectorsParagraphs"
        :key="'post-vector-' + pvIdx"
        class="narrative-paragraph"
        v-html="para"
      ></p>

      <!-- BS7: System outputs -->
      <div v-if="section.systemOutputs && section.systemOutputs.length" class="system-outputs">
        <div
          v-for="(output, soIdx) in section.systemOutputs"
          :key="'output-' + soIdx"
          class="system-output"
        >
          <p class="output-title"><strong><span v-html="output.title"></span></strong></p>
          <p class="output-text" v-html="output.text"></p>
        </div>
      </div>

      <!-- BS7: Parallel movements -->
      <div v-if="section.parallelMovements && section.parallelMovements.length" class="parallel-movements">
        <div
          v-for="(movement, pmIdx) in section.parallelMovements"
          :key="'movement-' + pmIdx"
          class="parallel-movement"
        >
          <p class="movement-title"><strong><span v-html="movement.title"></span></strong></p>
          <p class="movement-text" v-html="movement.text"></p>
        </div>
      </div>

      <!-- BS7: Post-movements image -->
      <div v-if="section.postMovementsImage" class="section-image-container">
        <img
          :src="section.postMovementsImage.src"
          :alt="section.postMovementsImage.alt"
          class="section-image"
        />
        <p v-if="section.postMovementsImage.caption" class="image-caption" v-html="section.postMovementsImage.caption"></p>
      </div>

      <!-- BS7: Post-movements paragraphs -->
      <p
        v-for="(para, pmpIdx) in section.postMovementsParagraphs"
        :key="'post-movements-' + pmpIdx"
        class="narrative-paragraph"
        v-html="para"
      ></p>

      <!-- BS7: Phase II outcomes -->
      <ul
        v-if="section.phaseIIOutcomes && section.phaseIIOutcomes.length"
        class="narrative-bullets"
      >
        <li
          v-for="(outcome, poIdx) in section.phaseIIOutcomes"
          :key="'phase2-outcome-' + poIdx"
          v-html="outcome"
        ></li>
      </ul>

      <!-- BS7: Phase III framework -->
      <ul
        v-if="section.phaseIIIFramework && section.phaseIIIFramework.length"
        class="narrative-bullets"
      >
        <li
          v-for="(item, pfIdx) in section.phaseIIIFramework"
          :key="'phase3-framework-' + pfIdx"
          v-html="item"
        ></li>
      </ul>

      <!-- BS7: Post-framework paragraphs -->
      <p
        v-for="(para, pffIdx) in section.postFrameworkParagraphs"
        :key="'post-framework-' + pffIdx"
        class="narrative-paragraph"
        v-html="para"
      ></p>

      <!-- BS7: Post-framework image -->
      <div v-if="section.postFrameworkImage" class="section-image-container">
        <img
          :src="section.postFrameworkImage.src"
          :alt="section.postFrameworkImage.alt"
          class="section-image"
        />
        <p v-if="section.postFrameworkImage.caption" class="image-caption" v-html="section.postFrameworkImage.caption"></p>
      </div>

      <!-- BS7: Final paragraphs -->
      <p
        v-for="(para, fpIdx) in section.finalParagraphs"
        :key="'final-para-' + fpIdx"
        class="narrative-paragraph"
        v-html="para"
      ></p>

      <!-- BS7: Methodology attributes -->
      <ul
        v-if="section.methodologyAttributes && section.methodologyAttributes.length"
        class="narrative-bullets"
      >
        <li
          v-for="(attr, maIdx) in section.methodologyAttributes"
          :key="'method-attr-' + maIdx"
          v-html="attr"
        ></li>
      </ul>

      <!-- BS7: Closing paragraphs (array) -->
      <p
        v-for="(para, cpIdx) in section.closingParagraphs"
        :key="'closing-para-' + cpIdx"
        class="narrative-paragraph"
        v-html="para"
      ></p>

      <!-- Second set of images (after postImageParagraphs) -->
      <div
        v-if="section.secondImages && section.secondImages.length"
        class="section-images-grid"
        :class="'grid-' + section.secondImages.length"
      >
        <div
          v-for="(img, imgIdx) in section.secondImages"
          :key="'secimg-' + imgIdx"
          class="grid-image-item"
        >
          <img
            :src="img.src"
            :alt="img.alt || section.title"
            class="grid-image"
          />
          <p v-if="img.caption" class="grid-image-caption" v-html="img.caption"></p>
        </div>
      </div>
      <p v-if="section.imagesCaption" class="images-grid-caption"
        v-html="section.imagesCaption">
      </p>
    </section>

    <!-- 4. STRATEGIC / INVESTOR SECTIONS (white titles, animated) -->
    <section class="strategic-sections">
      <!-- What This Stream Unlocks for Investors -->
      <div
        v-if="strategicContent.investorUnlocks"
        class="strategic-section animate fadeInUp"
      >
        <h3 class="strategic-title">What This Stream Unlocks for Investors</h3>
        <div class="strategic-content">
          <!-- Regular paragraphs: if there are bullet points, last paragraph becomes card intro; otherwise show all -->
          <p
            v-for="(
              para, idx
            ) in strategicContent.investorUnlocks.bulletPoints ? strategicContent.investorUnlocks.paragraphs.slice(0, -1) : strategicContent.investorUnlocks.paragraphs"
            :key="'inv-' + idx"
            v-html="para"
          ></p>

          <!-- Two-card layout for bullet sections -->
          <div
            v-if="
              strategicContent.investorUnlocks.bulletPoints ||
              strategicContent.investorUnlocks.secondBulletPoints
            "
            class="investor-cards-row"
          >
            <!-- Card 1: "Stories IP functions as" -->
            <div
              v-if="strategicContent.investorUnlocks.bulletPoints"
              class="investor-card"
            >
              <p class="card-intro" v-html="strategicContent.investorUnlocks.paragraphs[
                    strategicContent.investorUnlocks.paragraphs.length - 1
                  ]"></p>
              <ul class="strategic-bullets">
                <li
                  v-for="(bullet, bIdx) in strategicContent.investorUnlocks
                    .bulletPoints"
                  :key="'inv-b-' + bIdx"
                  v-html="bullet"
                ></li>
              </ul>
            </div>

            <!-- Card 2: "Each story activates" -->
            <div
              v-if="
                strategicContent.investorUnlocks.secondBulletPoints &&
                strategicContent.investorUnlocks.postBulletParagraphs
              "
              class="investor-card"
            >
              <p class="card-intro" v-html="strategicContent.investorUnlocks.postBulletParagraphs[0]"></p>
              <ul class="strategic-bullets">
                <li
                  v-for="(bullet, sbIdx) in strategicContent.investorUnlocks
                    .secondBulletPoints"
                  :key="'inv-sb-' + sbIdx"
                  v-html="bullet"
                ></li>
              </ul>
            </div>
          </div>

          <p
            v-if="strategicContent.investorUnlocks.closing"
            class="strategic-closing"
            v-html="strategicContent.investorUnlocks.closing"
          ></p>
          <p
            v-for="(para, cpIdx) in strategicContent.investorUnlocks.closingParagraphs"
            :key="'inv-closing-' + cpIdx"
            class="strategic-closing"
            v-html="para"
          ></p>
        </div>
      </div>

      <!-- How This Business Stream Forges the Myth -->
      <div
        v-if="strategicContent.forgesMyth"
        class="strategic-section animate fadeInUp"
      >
        <h3 class="strategic-title">
          How This Business Stream Forges the Myth
        </h3>
        <div class="strategic-content">
          <p
            v-for="(para, idx) in strategicContent.forgesMyth.paragraphs"
            :key="'myth-' + idx"
            v-html="para"
          ></p>
          <ul
            v-if="strategicContent.forgesMyth.bulletPoints"
            class="strategic-bullets"
          >
            <li
              v-for="(bullet, bIdx) in strategicContent.forgesMyth.bulletPoints"
              :key="'myth-b-' + bIdx"
              v-html="bullet"
            ></li>
          </ul>
          <p
            v-if="strategicContent.forgesMyth.closing"
            class="strategic-closing"
            v-html="strategicContent.forgesMyth.closing"
          ></p>
          <p
            v-for="(para, cpIdx) in strategicContent.forgesMyth.closingParagraphs"
            :key="'myth-closing-' + cpIdx"
            class="strategic-closing"
            v-html="para"
          ></p>
        </div>
      </div>

      <!-- How This Business Stream Differs From Comparable Models -->
      <div
        v-if="strategicContent.differentiator"
        class="strategic-section animate fadeInUp"
      >
        <h3 class="strategic-title">
          How This Business Stream Differs From Comparable Models on the Market
        </h3>
        <div class="strategic-content">
          <p
            v-for="(para, idx) in strategicContent.differentiator.paragraphs"
            :key="'diff-' + idx"
            v-html="para"
          ></p>
          <ul
            v-if="strategicContent.differentiator.bulletPoints"
            class="strategic-bullets"
          >
            <li
              v-for="(bullet, bIdx) in strategicContent.differentiator
                .bulletPoints"
              :key="'diff-b-' + bIdx"
              v-html="bullet"
            ></li>
          </ul>
          <p
            v-if="strategicContent.differentiator.closing"
            class="strategic-closing"
            v-html="strategicContent.differentiator.closing"
          ></p>
          <p
            v-for="(para, cpIdx) in strategicContent.differentiator.closingParagraphs"
            :key="'diff-closing-' + cpIdx"
            class="strategic-closing"
            v-html="para"
          ></p>
          <!-- BS7: Final bullet points -->
          <ul
            v-if="strategicContent.differentiator.finalBulletPoints"
            class="strategic-bullets"
          >
            <li
              v-for="(bullet, fbIdx) in strategicContent.differentiator.finalBulletPoints"
              :key="'diff-fb-' + fbIdx"
              v-html="bullet"
            ></li>
          </ul>
          <!-- BS7: Final closing -->
          <p
            v-if="strategicContent.differentiator.finalClosing"
            class="strategic-closing"
            v-html="strategicContent.differentiator.finalClosing"
          ></p>
        </div>
      </div>

      <!-- Investment Level & Business Stream Evolution by Milestones -->
      <div
        v-if="strategicContent.milestones"
        class="strategic-section milestones-section animate fadeInUp"
      >
        <h3 class="strategic-title">
          Investment Level & Business Stream Evolution by Milestones
        </h3>
        <p
          v-if="strategicContent.milestones.disclaimer"
          class="milestones-disclaimer"
        >
          {{ strategicContent.milestones.disclaimer }}
        </p>
        <div class="milestones-grid">
          <div
            v-for="(milestone, mIdx) in strategicContent.milestones.items"
            :key="'ms-' + mIdx"
            class="milestone-item"
          >
            <h4 class="milestone-title">{{ milestone.title }}</h4>
            <p class="milestone-range">{{ milestone.range }}</p>
            <p class="milestone-description">{{ milestone.description }}</p>
          </div>
        </div>
        <p v-if="strategicContent.milestones.footer" class="milestones-footer">
          {{ strategicContent.milestones.footer }}
        </p>
      </div>
    </section>

    <!-- 5. CLOSING NARRATIVE SECTION -->
    <section v-if="closingSection" class="closing-section animate fadeInUp">
      <h3 class="closing-title" v-if="closingSection.title">
        <span class="tilde">~</span> {{ closingSection.title }}
        <span class="tilde">~</span>
      </h3>
      <div class="closing-content">
        <p
          v-for="(para, idx) in closingSection.paragraphs"
          :key="'close-' + idx"
          v-html="para"
        ></p>
      </div>
      <!-- Optional closing image -->
      <div v-if="closingSection.image" class="section-image-container">
        <img
          :src="closingSection.image"
          :alt="closingSection.imageAlt || 'Closing'"
          class="section-image"
        />
        <p v-if="closingSection.imageCaption" class="image-caption">
          {{ closingSection.imageCaption }}
        </p>
      </div>
      <!-- Post-image paragraphs in closing section -->
      <div v-if="closingSection.postImageParagraphs && closingSection.postImageParagraphs.length" class="closing-content">
        <p
          v-for="(para, idx) in closingSection.postImageParagraphs"
          :key="'closepost-' + idx"
        >
          {{ para }}
        </p>
      </div>
    </section>

    <!-- 6. ENTRY ACCESS LINK -->
    <div v-if="nextStreamLabel" class="entry-link-container animate fadeInUp">
      <p class="entry-prompt">
        when you are ready, follow the thread into
        <span
          class="entry-link"
          @click="$emit('navigateToStream', nextStreamId)"
        >
          {{ nextStreamLabel }}
          <svg
            class="entry-arrow"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </span>
      </p>
    </div>

    <!-- 6b. CLOSING ACCESS NOTE (no link, used for final stream) -->
    <div v-if="closingAccessNote" class="entry-link-container animate fadeInUp">
      <p class="entry-prompt">
        {{ closingAccessNote.title }}
      </p>
      <p class="entry-prompt entry-subtitle">
        {{ closingAccessNote.subtitle }}
      </p>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";

export default {
  name: "BusinessStreamContent",

  props: {
    streamNumber: {
      type: [Number, String],
      required: true,
    },
    streamName: {
      type: String,
      required: true,
    },
    streamSubtitle: {
      type: String,
      default: "",
    },
    streamTagline: {
      type: String,
      default: "",
    },
    streamTaglineLine1: {
      type: String,
      default: "",
    },
    streamTaglineLine2: {
      type: String,
      default: "",
    },
    introText: {
      type: [String, Array],
      default: "",
    },
    introBulletPoints: {
      type: Array,
      default: () => [],
    },
    introClosing: {
      type: [String, Array],
      default: "",
    },
    introPhaseBullets: {
      type: Array,
      default: () => [],
    },
    introFinalParagraph: {
      type: String,
      default: "",
    },
    introImages: {
      type: Array,
      default: () => [],
      // Optional images after intro text: [{ src, alt, caption }]
    },
    narrativeSections: {
      type: Array,
      default: () => [],
      // Each section: { title, paragraphs: [], bulletPoints: [], image?, imageAlt?, imageCaption? }
    },
    strategicContent: {
      type: Object,
      default: () => ({}),
      // { investorUnlocks: { paragraphs, bulletPoints }, forgesMyth: { paragraphs }, differentiator: { paragraphs }, milestones: { disclaimer, items: [{ title, range, description }], footer } }
    },
    closingSection: {
      type: Object,
      default: null,
      // { title?, paragraphs: [], image?, imageAlt?, imageCaption? }
    },
    nextStreamLabel: {
      type: String,
      default: "",
    },
    nextStreamId: {
      type: String,
      default: "",
    },
    nextStreamNumber: {
      type: [Number, String],
      default: "",
    },
    closingAccessNote: {
      type: Object,
      default: null,
    },
  },

  emits: ["navigateToStream"],

  setup(props) {
    const introTextArray = computed(() => {
      if (Array.isArray(props.introText)) {
        return props.introText;
      }
      return props.introText ? [props.introText] : [];
    });

    return {
      introTextArray,
    };
  },
};
</script>

<style scoped>
.business-stream-content {
  max-width: 900px;
  margin: 0 auto;
  padding: var(--largeSpace);
  overflow: hidden;
}

/* 1. STREAM HEADER — uses same global classes as character pages */
.stream-header {
  text-align: center;
  margin-bottom: calc(var(--doubleSpace) * 1.5);
  padding-bottom: calc(var(--doubleSpace) * 1.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

/* Exact copy from CeciliaCharacter.vue scoped styles */
.intro-text {
  font-size: var(--smallFontSize);
  line-height: 1.4;
  margin-bottom: calc(var(--space) * 6.5);
  position: relative;
}
.intro-text::after {
  content: "";
  position: absolute;
  top: calc(var(--regularFontSize) * 2);
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 1px;
  background: var(--text-white);
}

/* Exact copy from CeciliaCharacter.vue */
.largest-TextMag {
  font-size: calc(var(--giganticFontSize) * 1.2) !important;
  font-weight: 630;
  text-transform: uppercase;
}

/* Tagline — cascade layout matching character pages (From Cecilia / to a real girl) */
.stream-tagline {
  width: 100%;
  position: relative;
  margin-top: calc(var(--largeSpace) * 2.5);
  height: 110px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.tagline-line1 {
  white-space: nowrap;
}

.tagline-line1 h2 {
  transform: translateX(-30%);
}

.tagline-line2 {
  white-space: nowrap;
}

.tagline-line2 h2 {
  transform: translateX(30%);
}

/* 2. INTRO SECTION */
.intro-section {
  margin-bottom: var(--doubleSpace);
}

.intro-paragraph {
  color: var(--text-white);
  margin-bottom: var(--space);
  line-height: calc(var(--regularFontSize) * 1.55);
}

/* 3. NARRATIVE SECTIONS */
.narrative-section {
  margin-bottom: calc(var(--doubleSpace) * 2);
  padding: var(--largeSpace) 0;
}

.narrative-title {
  font-family: "Playfair", "PlayfairStatic", serif;
  font-style: italic;
  font-size: var(--bigFontSize);
  font-weight: 500;
  color: var(--lilac-300);
  text-align: center;
  margin-bottom: calc(var(--doubleSpace) * 1.3);
}

.narrative-title .title-text {
}

.narrative-title .tilde-left,
.narrative-title .tilde-right {
  font-style: normal;
  color: var(--lilac-300);
}
.narrative-subtitle {
  font-size: var(--smallFontSize);
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: var(--grey-lighter);
  text-align: center;
  margin-bottom: var(--space);
}

.narrative-content p {
  color: var(--text-white);
  margin-bottom: var(--doubleSpace);
  line-height: calc(var(--regularFontSize) * 1.55);
}

.narrative-content {
  margin-bottom: var(--doubleSpace);
}

/* Roadmap container styling — tighter top margin since title is directly above */
.roadmap-container {
  margin: 0 0 var(--space) 0;
  padding: 0 0 var(--space) 0;
}

/* Roadmap placeholder sections — reduce bottom spacing on parent section */
.narrative-section:has(> .roadmap-container) {
  padding-bottom: 0;
}

/* Section closing paragraph */
.section-closing {
  color: var(--text-white);
  margin-top: calc(var(--doubleSpace) * 1.2);
}

.narrative-bullets {
  list-style: none;
  padding: 0;
  margin: calc(var(--doubleSpace) * 1.5) 0;
}

.narrative-bullets li {
  position: relative;
  padding-left: 1.5em;
  line-height: calc(var(--regularFontSize) * 1.45);
  margin-bottom: var(--smallSpace);
}

.narrative-bullets li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--lilac-300);
}

/* Intro bullet list - same style as narrative bullets */
.intro-bullet-list {
  list-style: none;
  padding: 0;
  margin: var(--doubleSpace) 0 calc(var(--doubleSpace) * 1.5) 0;
}

.intro-bullet-list li {
  position: relative;
  padding-left: 1.5em;
  line-height: calc(var(--regularFontSize) * 1.45);
  margin-bottom: var(--smallSpace);
}

.intro-bullet-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--lilac-300);
}

.intro-closing {
  margin-top: var(--largeSpace);
}

/* BS7: Comparison lines - emphasized text */
.comparison-lines {
  margin: var(--doubleSpace) 0;
  padding: var(--space) 0;
}

.comparison-line {
  font-style: italic;
  color: var(--text-white);
  margin-bottom: var(--smallSpace);
  font-size: var(--regularFontSize);
}

/* BS7: Encoding descriptions */
.encoding-descriptions {
  margin: var(--doubleSpace) 0;
}

.encoding-description {
  margin-bottom: var(--largeSpace);
  line-height: 1.6;
}

/* BS7: Research vectors */
.research-vectors {
  margin: var(--doubleSpace) 0;
}

.research-vector {
  margin-bottom: calc(var(--doubleSpace) * 1.5);
  padding-left: var(--space);
  border-left: 2px solid var(--lilac-300);
}

.vector-title {
  color: var(--text-white);
  margin-bottom: var(--smallSpace);
}

.vector-text {
  color: var(--text-white);
  line-height: 1.55;
  opacity: 0.9;
}

/* BS7: System outputs */
.system-outputs {
  margin: var(--doubleSpace) 0;
}

.system-output {
  margin-bottom: calc(var(--doubleSpace) * 1.5);
  padding-left: var(--space);
  border-left: 2px solid var(--lilac-300);
}

.output-title {
  color: var(--text-white);
  margin-bottom: var(--smallSpace);
}

.output-text {
  color: var(--text-white);
  line-height: 1.55;
  opacity: 0.9;
}

/* BS7: Parallel movements */
.parallel-movements {
  margin: var(--doubleSpace) 0;
}

.parallel-movement {
  margin-bottom: var(--largeSpace);
  padding-left: var(--space);
  border-left: 2px solid var(--lilac-300);
}

.movement-title {
  color: var(--text-white);
  margin-bottom: var(--smallSpace);
}

.movement-text {
  color: var(--text-white);
  line-height: 1.55;
  opacity: 0.9;
}

/* 4. STRATEGIC SECTIONS */
.strategic-sections {
  margin: var(--doubleSpace) 0;
}

.strategic-section {
  margin-bottom: calc(var(--doubleSpace) * 1.5);
  padding: var(--largeSpace) 0;
}

.strategic-title {
  font-size: var(--bigFontSize);
  font-weight: 600;
  color: var(--text-white);
  text-align: center;
  margin-bottom: var(--doubleSpace);
  margin-top: var(--doubleSpace);
}

.strategic-content p {
  color: var(--text-white);
  margin-bottom: var(--largeSpace);
  line-height: calc(var(--regularFontSize) * 1.55);
}

.strategic-bullets {
  list-style: none;
  padding: 0;
  margin: calc(var(--doubleSpace) * 1.2) 0;
}

.strategic-bullets li {
  position: relative;
  padding-left: 1.5em;
  margin-bottom: var(--smallSpace);
  line-height: 1.6;
}

.strategic-bullets li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--text-white);
}

/* Strategic Closing - normal paragraph styling */
.strategic-closing {
  color: var(--text-white);
  margin-top: var(--doubleSpace);
  text-align: left;
}

/* Milestones */
.milestones-disclaimer,
.milestones-footer {
  font-size: var(--smallFontSize);
  font-style: italic;
  color: var(--grey-lighter);
  margin-bottom: var(--largeSpace);
}

.milestones-grid {
  display: flex;
  flex-direction: column;
  gap: var(--largeSpace);
  margin: var(--doubleSpace) 0;
}

.milestone-item {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--grey-darkestest);
  border-left: 3px solid var(--lilac-300);
  padding: var(--largeSpace);
  border-radius: var(--border-radius-medium);
}

.milestone-title {
  color: var(--text-white);
  margin: 0 0 var(--smallSpace) 0;
}

.milestone-range {
  color: var(--gold-300);
  margin: 0 0 var(--space) 0;
}

.milestone-description {
  font-size: var(--smallFontSize);
  color: var(--grey-lighter);
  margin: 0;
}

/* 5. CLOSING SECTION */
.closing-section {
  margin: var(--doubleSpace) 0;
  padding: var(--largeSpace) 0;
}

.closing-title {
  font-family: "Playfair", "PlayfairStatic", serif;
  font-style: italic;
  font-size: var(--largerFontSize);
  color: var(--lilac-300);
  text-align: center;
  margin-bottom: var(--space);
  margin-bottom: var(--doubleSpace);
}

.closing-title .tilde {
  font-style: normal;
  color: var(--lilac-300);
  margin: 0 0.3em;
}

.closing-content p {
  margin-bottom: var(--space);
  text-align: center;
}

/* Section images */
.section-image-container {
  margin: var(--doubleSpace) 0;
  text-align: center;
}

.section-image {
  max-width: 100%;
  height: auto;
  border-radius: var(--border-radius-medium);
}

.image-caption {
  font-style: italic;
  text-align: center;
  color: var(--grey-dark);
  font-size: var(--smallerFontSize);
  line-height: calc(var(--smallerFontSize) * 1.45);
}

/* Image grids for artefacts */
.section-images-grid {
  display: grid;
  gap: var(--space);
  margin: var(--doubleSpace) 0;
}

.section-images-grid.grid-1 {
  grid-template-columns: 1fr;
  max-width: 100%;
}

.section-images-grid.grid-1.vertical {
  max-width: 60%;
  margin-left: auto;
  margin-right: auto;
}

.section-images-grid.grid-2 {
  grid-template-columns: repeat(2, 1fr);
}

.section-images-grid.grid-4 {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, auto);
}

.section-images-grid.grid-5 {
  grid-template-columns: repeat(2, 1fr);
}

.section-images-grid.grid-5 {
  grid-template-columns: repeat(2, 1fr);
}

.grid-image-item {
  text-align: center;
  padding: var(--space);
}

.grid-image {
  width: 100%;
  height: auto;
  border-radius: var(--border-radius-medium);
}

.grid-image-caption {
  font-style: italic;
  text-align: center;
  color: var(--grey-dark);
  font-size: var(--smallerFontSize);
  line-height: calc(var(--smallerFontSize) * 1.45);
  white-space: pre-line;
}

.images-grid-caption {
  margin-top: var(--largeSpace);
  font-style: italic;
  text-align: center;
  color: var(--grey-dark);
  font-size: var(--smallFontSize);
}

@media (max-width: 600px) {
  .section-images-grid.grid-1.vertical {
    max-width: 90%;
  }
  .section-images-grid.grid-2,
  .section-images-grid.grid-4,
  .section-images-grid.grid-5 {
    grid-template-columns: 1fr;
  }
}

/* 6. ENTRY LINK */
.entry-link-container {
  text-align: center;
  margin-top: var(--doubleSpace);
  padding: var(--largeSpace) 0;
}

.entry-prompt {
  font-family: "Playfair", "PlayfairStatic", serif;
  font-style: italic;
  color: var(--grey-lighter);
}

.entry-subtitle {
  font-size: 0.85em;
  margin-top: var(--smallSpace);
  color: var(--grey-light);
}

.entry-link {
  color: var(--lilac-300);
  cursor: pointer;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: var(--smallSpace);
}

.entry-link:hover {
  color: var(--text-white);
  text-decoration: underline;
}

.entry-arrow {
  width: 1.2em;
  height: 1.2em;
  transition: transform 0.2s ease;
}

.entry-link:hover .entry-arrow {
  transform: translateX(4px);
}

/* INVESTOR CARDS */
.investor-cards-row {
  display: flex;
  flex-direction: row;
  gap: var(--largeSpace);
  margin: var(--doubleSpace) 0;
}

.investor-card {
  flex: 1;
  background: rgba(27, 27, 27, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius-medium);
  padding: var(--largeSpace);
  backdrop-filter: blur(10px);
}

.investor-card .card-intro {
  font-family: "Playfair", "PlayfairStatic", serif;
  font-style: italic;
  color: var(--lilac-300);
  margin-bottom: var(--space);
}

.investor-card .strategic-bullets {
  margin-top: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .business-stream-content {
    padding: 0;
  }

  .largest-TextMag {
    font-size: calc(var(--giganticFontSize) * 0.9) !important;
  }

  .stream-tagline h2 {
    font-size: var(--largerFontSize) !important;
  }

  .stream-tagline {
    height: 80px;
  }

  .tagline-line1 h2,
  .tagline-line2 h2 {
    transform: none;
  }

  .narrative-title {
    font-size: var(--largeFontSize);
  }

  .closing-title {
    font-size: var(--largeFontSize);
  }

  .strategic-title {
    font-size: var(--largeFontSize);
  }

  .investor-cards-row {
    flex-direction: column;
  }
}

/* =============================================
   HIGHLIGHT SYSTEM — 3 font styles for inline highlights
   Based on existing site typography:
   - hl-a: "by the time..." style (Inter body, italic, lilac)
   - hl-b: "her training" style (Inter body, bold, white)
   - hl-c: "gave way to mastery" style (Playfair, italic, lilac)
   ============================================= */

/* Highlight A: Body italic, lilac — gentle emphasis */
:deep(.hl-a) {
  font-style: italic;
  color: var(--lilac-400);
}

/* Highlight B: Body bold, white — strong anchor */
:deep(.hl-b) {
  font-weight: 600;
  color: var(--text-white);
}

/* Highlight C: purple emphasis — key statements */
:deep(.hl-c) {
  color: var(--purple-dark-tint-4);
}

/* Semi-bold bullet variants for strategic sections */
:deep(.bullet-lilac) {
  font-weight: 500;
  color: var(--lilac-300);
}

:deep(.bullet-white) {
  font-weight: 500;
  color: var(--text-white);
}

/* Macro header: for section titles that need more weight (e.g. Three Artifact Categories) */
.narrative-title.macro-header {
  font-size: var(--bigFontSize);
  font-weight: 600;
  color: var(--lilac-300);
  text-align: center;
  margin-bottom: 0;
  margin-top: calc(var(--doubleSpace) * 2);
}

/* Macro header sections (empty content, just a parent title) — reduce bottom spacing */
.narrative-section:has(> .narrative-title.macro-header) {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

/* Chapter header: sub-headers below macro (e.g. Permanent, Limited, Collector's) */
.narrative-title.chapter-header {
  font-size: var(--largerFontSize);
  font-weight: 400;
  margin-top: 0;
}

/* Phase headers for 6+1 rhythm cycle */
:deep(.phase-header) {
  font-weight: 600;
  color: var(--text-white);
  font-size: calc(var(--regularFontSize) * 1.08);
  display: block;
  margin-top: calc(var(--doubleSpace) * 1.5);
  margin-bottom: var(--smallSpace);
}

/* Step number in rhythm cycle */
:deep(.step-label) {
  font-style: italic;
  color: var(--lilac-300);
}

/* Seventh step — now styled like phase headers, kept for backward compat */
:deep(.seventh-step) {
  font-style: italic;
  color: var(--lilac-400);
  opacity: 0.85;
}

/* Subtle phase divider */
:deep(.phase-divider) {
  border: none;
  border-top: 1px solid rgba(145, 117, 188, 0.15);
  margin: calc(var(--doubleSpace) * 1.2) 0;
}

/* Hide default purple dot on li items that contain a phase-divider or liminal-divider (dot would misalign to the hr) */
.narrative-bullets li:has(> .phase-divider)::before,
.narrative-bullets li:has(> .liminal-divider)::before {
  display: none;
}

/* Re-add purple dot aligned to the phase header text after the divider */
:deep(.phase-header),
:deep(.seventh-step) {
  position: relative;
}

:deep(.phase-divider ~ .phase-header)::before {
  content: "•";
  position: absolute;
  left: -1.5em;
  color: var(--lilac-300);
}

/* Re-add purple dot for liminal-divider items (BS6 liminal encounters) */
:deep(.liminal-divider ~ .textColour)::before {
  content: "•";
  position: absolute;
  left: -1.5em;
  color: var(--lilac-300);
}

:deep(.liminal-divider ~ .textColour) {
  position: relative;
}

/* The seventh step uses a + bullet instead of a dot */
:deep(.phase-header.phase-plus)::before {
  content: "+";
}

/* Liminal encounter divider (stream 6) */
:deep(.liminal-divider) {
  border: none;
  border-top: 1px solid rgba(145, 117, 188, 0.12);
  margin: var(--doubleSpace) 0;
}

/* Arc labels (Cecilia Arc, Patricia Arc, The Path Arc) - bolder + slightly larger */
:deep(.arc-label) {
  font-weight: 600;
  color: var(--lilac-300);
  font-size: calc(var(--regularFontSize) * 1.08);
}
</style>
